import { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useLeasingCompaniesApi } from "../../http/leasingCompanies/api";
import { LeasingCompany } from "../../http/leasingCompanies/types";
import { PaginationQueryWithFields } from "../../types";
import { isWithinLast48h } from "../../types/utils";
import SvgEdit from "../icons/Edit";
import Header from "../ui/Header";
import { Action, getDeleteAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import CreateLeasingCompanyButton from "./CreateLeasingCompanyButton";
import DeleteLeasingCompanyDialog from "./DeleteLeasingCompanyDialog";
import LeasingCompanyHistoryDialog from "./LeasingCompanyHistoryDialog";

const LEASING_COMPANY_FIELDS = ["id", "createdAt", "name", "address", "email", "phoneNumber", "versionNumber"] as const;
type LeasingCompanyFiels = (typeof LEASING_COMPANY_FIELDS)[number];
type LeasingCompanySearch = Pick<LeasingCompany, LeasingCompanyFiels>;

const LeasingCompanies: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const leasingCompaniesApi = useLeasingCompaniesApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const navigate = useNavigate();
  const aborter = useRef(new AbortController());
  const [itemToDelete, setItemToDelete] = useState<LeasingCompanySearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<LeasingCompanySearch>();

  const query: PaginationQueryWithFields<LeasingCompanySearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: LEASING_COMPANY_FIELDS,
  };

  const result = leasingCompaniesApi.useAdminLeasingCompanies(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: LeasingCompanySearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.leasingCompanies.update) {
      actions.push({
        title: i18n.t.common.edit,
        onClick: () => navigate("/admin/leasing_companies/" + item.id),
        icon: SvgEdit,
      });
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.leasingCompanies.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <Header title={i18n.t.leasingCompanies.plural} />
      <Panel>
        <Table<LeasingCompanySearch>
          filters={filters}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          buttons={{ main: userSettings.permissions.leasingCompanies.insert && <CreateLeasingCompanyButton /> }}
          head={
            <>
              <Table.Th fieldName="leasing.name" title={i18n.t.common.name} order={orderOptions} />
              <Table.Th title={i18n.t.common.address} fieldName="leasing.address" order={orderOptions} />
              <Table.Th title={i18n.t.common.email} fieldName="leasing.email" order={orderOptions} />
              <Table.Th title={i18n.t.common.phoneNumber} fieldName="leasing.phoneNumber" order={orderOptions} />
              <th></th>
            </>
          }
          onRowClick={(item) => navigate("/admin/leasing_companies/" + item.id)}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                {item.name}
              </td>
              <td>{item.address}</td>
              <td>{item.email}</td>
              <td>{item.phoneNumber}</td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </Panel>

      {!!itemToDelete && !!itemToDelete.id && (
        <DeleteLeasingCompanyDialog leasingCompanyId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToShowVersions && !!itemToShowVersions.id && (
        <LeasingCompanyHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );
};

export default LeasingCompanies;
